import { Pipe, PipeTransform } from '@angular/core';
import { toDisplayDate } from '../utils/date.utils';

/**
 * Formats date strings for display throughout the Logbook module.
 *
 * Usage:
 *   {{ record.date | adnDate }}              → "Jan 5, 2026"
 *   {{ record.date | adnDate:'datetime' }}   → "Jan 5, 2026, 3:45 PM"
 *   {{ record.date | adnDate:'range':endDate }} → "Jan 5, 2026 - Jan 10, 2026"
 *
 * Calendar dates (Postgres `date` columns, which the UTC Hub serializes as
 * "2026-07-29T00:00:00.000Z") are pinned to local midnight before formatting,
 * so they can't roll back a day in US timezones. `datetime` keeps normal
 * timezone conversion — those values are genuine instants.
 */
@Pipe({
  name: 'adnDate',
  standalone: true
})
export class DateFormatPipe implements PipeTransform {
  transform(
    value: string | Date | null | undefined,
    format: 'date' | 'datetime' | 'range' = 'date',
    endDate?: string | Date
  ): string {
    if (!value) return '';

    // 'datetime' formats a real instant, so it must keep timezone conversion.
    // 'date'/'range' render a calendar day and must not shift across midnight.
    const date = format === 'datetime'
      ? (value instanceof Date ? value : new Date(value))
      : toDisplayDate(value);

    if (!date || isNaN(date.getTime())) return '';

    switch (format) {
      case 'datetime':
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });

      case 'range':
        if (!endDate) return this.transform(value, 'date');
        const end = endDate instanceof Date ? endDate : new Date(endDate);
        if (isNaN(end.getTime())) return this.transform(value, 'date');
        return `${this.transform(value, 'date')} - ${this.transform(endDate, 'date')}`;

      default:
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
    }
  }
}
